import { useState, useEffect } from "react";
import { Switch, Route, Router as WouterRouter, useLocation, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { MainLayout } from "@/components/layout/main-layout";
import { ErrorBoundary } from "@/components/error-boundary";
import Login from "@/pages/login";
import { getToken } from "@/lib/auth";

// Pages
import Dashboard from "@/pages/dashboard";
import GenelBakis from "@/pages/genel-bakis";
import Firsatlar from "@/pages/firsatlar";
import NewDomain from "@/pages/domains/new";
import DomainDetail from "@/pages/domains/detail";
import DomainClusters from "@/pages/domains/clusters";
import PageDetail from "@/pages/pages/detail";
import Planning from "@/pages/planning";
import ContentOpportunities from "@/pages/content-opportunities";
import ContentStudio from "@/pages/content-studio";
import Assistant from "@/pages/assistant";
import Admin from "@/pages/admin";
import Account from "@/pages/account";
import { AssistantWidget } from "@/components/assistant-widget";
import { AssistantEffects } from "@/components/assistant-effects";
import { CostConfirmHost } from "@/components/cost-confirm";
import { ConfirmDialogHost } from "@/components/confirm-dialog";
import ContentPlanner from "@/pages/content-planner";
import PageStrengthen from "@/pages/page-strengthen";
import ContentTranslate from "@/pages/content-translate";
import TrafficAnalysis from "@/pages/traffic-analysis";
import QuickAnalysis from "@/pages/quick-analysis";
import BacklinkAudit from "@/pages/backlink-audit";
import TechAudit from "@/pages/tech-audit";
import Nobetci from "@/pages/nobetci";
import AiBotErisim from "@/pages/ai-bot-erisim";
import BacklinkFirsat from "@/pages/backlink-firsat";
import Sxo from "@/pages/sxo";
import Tazelik from "@/pages/tazelik";
import AiFirsatlar from "@/pages/ai-firsatlar";
import SchemaAgent from "@/pages/schema-agent";
import EeatAgent from "@/pages/eeat-agent";
import MimariAgent from "@/pages/mimari-agent";
import YolHaritasi from "@/pages/yol-haritasi";
import IcerikGunlugu from "@/pages/icerik-gunlugu";
import RankTracking from "@/pages/rank-tracking";
import IndexRadar from "@/pages/index-radar";
import KeywordResearch from "@/pages/keyword-research";
import SeoReport from "@/pages/seo-report";
import { PrivacyPage, TermsPage } from "@/pages/legal";
import NoPlan from "@/pages/no-plan";
import { ConnectionBanner } from "@/components/connection-banner";
import SharedReport from "@/pages/shared-report";
import BotLog from "@/pages/bot-log";
import ContentDetail from "@/pages/content-detail";
import PlannerBrief from "@/pages/planner-brief";
import PlannerAnalysis from "@/pages/planner-analysis";
import TranslationDetail from "@/pages/translation-detail";
import Settings from "@/pages/settings";

const queryClient = new QueryClient();

function Router() {
  return (
    <Switch>
      {/* Çekidüzen Adım 0: "/" = Genel Bakış (çok siteli→filo, tek siteli→kokpit); /kokpit = site kokpiti */}
      <Route path="/" component={GenelBakis} />
      <Route path="/kokpit" component={Dashboard} />
      <Route path="/firsatlar" component={Firsatlar} />
      <Route path="/domains" component={GenelBakis} />
      <Route path="/domains/new" component={NewDomain} />
      <Route path="/domains/:id" component={DomainDetail} />
      <Route path="/domains/:id/clusters" component={DomainClusters} />
      <Route path="/pages/:id" component={PageDetail} />
      {/* Aksiyon Planı + İçerik Takvimi tek "Planlama" sayfasında birleşti — eski rotalar yönlenir */}
      <Route path="/planning" component={Planning} />
      <Route path="/content-calendar">{() => <Redirect to="/planning" replace />}</Route>
      <Route path="/content-opportunities" component={ContentOpportunities} />
      <Route path="/content-studio" component={ContentStudio} />
      <Route path="/assistant" component={Assistant} />
      <Route path="/action-plan">{() => <Redirect to="/planning" replace />}</Route>
      <Route path="/admin" component={Admin} />
      <Route path="/account" component={Account} />
      <Route path="/content/:id" component={ContentDetail} />
      <Route path="/planner-analysis" component={PlannerAnalysis} />
      <Route path="/planner-brief/:kw" component={PlannerBrief} />
      <Route path="/translation/:id" component={TranslationDetail} />
      <Route path="/content-planner" component={ContentPlanner} />
      <Route path="/page-strengthen" component={PageStrengthen} />
      <Route path="/page-strengthen/:pid" component={PageStrengthen} />
      <Route path="/content-translate" component={ContentTranslate} />
      <Route path="/traffic-analysis" component={TrafficAnalysis} />
      <Route path="/quick-analysis" component={QuickAnalysis} />
      <Route path="/backlink-audit" component={BacklinkAudit} />
      <Route path="/tech-audit" component={TechAudit} />
      <Route path="/nobetci" component={Nobetci} />
      <Route path="/ai-bot-erisim" component={AiBotErisim} />
      <Route path="/backlink-firsat" component={BacklinkFirsat} />
      <Route path="/sxo" component={Sxo} />
      <Route path="/tazelik" component={Tazelik} />
      <Route path="/ai-firsatlar" component={AiFirsatlar} />
      <Route path="/schema-agent" component={SchemaAgent} />
      <Route path="/eeat-agent" component={EeatAgent} />
      <Route path="/mimari-agent" component={MimariAgent} />
      <Route path="/yol-haritasi" component={YolHaritasi} />
      <Route path="/rank-tracking" component={RankTracking} />
      <Route path="/index-radar" component={IndexRadar} />
      <Route path="/icerik-gunlugu" component={IcerikGunlugu} />
      <Route path="/keyword-research" component={KeywordResearch} />
      <Route path="/seo-report" component={SeoReport} />
      <Route path="/portfolio">{() => <Redirect to="/" replace />}</Route>
      <Route path="/bot-log" component={BotLog} />
      <Route path="/settings" component={Settings} />
      <Route component={NotFound} />
    </Switch>
  );
}

// Rota değişince ErrorBoundary otomatik sıfırlansın (geçiş hatasında takılı kalmasın)
function RoutedBoundary() {
  const [location] = useLocation();
  return (
    <ErrorBoundary resetKey={location}>
      <Router />
    </ErrorBoundary>
  );
}

function App() {
  // Auth kapısı: token yoksa uygulama yerine login ekranı (MainLayout'suz) gösterilir.
  const [authed, setAuthed] = useState(() => !!getToken());
  // PAKET KAPISI: paketi olmayan hesap uygulamayı hiç görmez, "Ücretsiz Plan" ekranına düşer.
  // null = henüz bilinmiyor (ekran çizilmez, yanıp sönme olmasın).
  const [planlı, setPlanlı] = useState<boolean | null>(null);
  useEffect(() => {
    if (!authed) { setPlanlı(null); return; }
    void fetch("/api/auth/me")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { user?: { packageId?: number | null; isAdmin?: boolean } } | null) => {
        if (!d?.user) { setPlanlı(true); return; } // yanıt okunamadıysa engelleme (sunucu zaten 402 verir)
        setPlanlı(d.user.isAdmin === true || d.user.packageId != null);
      })
      .catch(() => setPlanlı(true)); // ağ hatasında kilitleme — asıl kapı sunucuda
  }, [authed]);

  // Hukuki metinler giriş GEREKTİRMEZ (KVKK: aydınlatma metni kayıttan önce okunabilmeli)
  if (window.location.pathname === "/gizlilik") return <PrivacyPage />;
  if (window.location.pathname === "/kosullar") return <TermsPage />;
  // Paylaşımlı müşteri raporu: /r/<token> — login gerektirmez (white-label sayfa)
  const sharedMatch = window.location.pathname.match(/^\/r\/([A-Za-z0-9_-]{10,})$/);
  if (sharedMatch) return <SharedReport token={sharedMatch[1]} />;

  if (!authed) {
    return <><ConnectionBanner /><Login onSuccess={() => setAuthed(true)} /></>;
  }

  if (planlı === null) return null;          // paket durumu gelene kadar boş ekran
  if (planlı === false) return <NoPlan />;   // paketsiz hesap: uygulama hiç yüklenmez

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ConnectionBanner />
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <MainLayout>
            <RoutedBoundary />
            <AssistantWidget />
            <AssistantEffects />
            <CostConfirmHost />
            <ConfirmDialogHost />
          </MainLayout>
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
